import { z } from 'zod';

// Bank statement CSV parsing. Pure and browser-safe: the web app uses it for the import preview, the server
// to import. Canadian banks export varied layouts, so columns are mapped explicitly (and saved per account).

export const DATE_FORMATS = ['YYYY-MM-DD', 'YYYY/MM/DD', 'DD/MM/YYYY', 'MM/DD/YYYY', 'DD-MM-YYYY'] as const;

export const BankCsvMapping = z
  .object({
    /** Lines to skip at the top (header). */
    skipRows: z.int().min(0).max(20).default(1),
    dateColumn: z.int().min(0),
    dateFormat: z.enum(DATE_FORMATS),
    descriptionColumns: z.array(z.int().min(0)).min(1),
    /** Either one signed amount column (deposits positive)… */
    amountColumn: z.int().min(0).optional(),
    /** …or separate withdrawal and deposit columns. */
    withdrawalColumn: z.int().min(0).optional(),
    depositColumn: z.int().min(0).optional(),
    referenceColumn: z.int().min(0).optional(),
    decimalComma: z.boolean().default(false),
  })
  .refine((m) => (m.amountColumn !== undefined) !== (m.withdrawalColumn !== undefined && m.depositColumn !== undefined), {
    message: 'Map either an amount column, or both withdrawal and deposit columns',
    path: ['amountColumn'],
  });
export type BankCsvMapping = z.infer<typeof BankCsvMapping>;

export interface StatementLine {
  row: number;
  date: string;
  description: string;
  reference: string | null;
  /** Positive for deposits, negative for withdrawals. */
  amountCents: number;
}

/** Splits CSV text into rows (delimiter auto-detected among , ; and tab; quotes and "" escapes supported). */
export function parseCsv(text: string): string[][] {
  const clean = text.replace(/^﻿/, '');
  const firstLine = clean.split(/\r?\n/).find((l) => l.trim() !== '') ?? '';
  const delimiter = [',', ';', '\t'].map((d) => [d, countOutsideQuotes(firstLine, d)] as const).sort((a, b) => b[1] - a[1])[0]![0];

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < clean.length; i++) {
    const c = clean[i]!;
    if (quoted) {
      if (c === '"' && clean[i + 1] === '"') {
        field += '"';
        i++;
      } else if (c === '"') quoted = false;
      else field += c;
    } else if (c === '"') quoted = true;
    else if (c === delimiter) {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && clean[i + 1] === '\n') i++;
      row.push(field);
      if (row.some((f) => f.trim() !== '')) rows.push(row.map((f) => f.trim()));
      row = [];
      field = '';
    } else field += c;
  }
  row.push(field);
  if (row.some((f) => f.trim() !== '')) rows.push(row.map((f) => f.trim()));
  return rows;
}

function countOutsideQuotes(line: string, char: string): number {
  let count = 0;
  let quoted = false;
  for (const c of line) {
    if (c === '"') quoted = !quoted;
    else if (c === char && !quoted) count++;
  }
  return count;
}

/** Parses "1 234,56", "1,234.56", "(12.00)", "-12,00 $" into cents; null if empty, throws if invalid. */
export function parseAmount(raw: string, decimalComma: boolean): number | null {
  let s = raw.replace(/[\s  $]/g, '');
  if (s === '') return null;
  let negative = false;
  if (/^\(.*\)$/.test(s)) {
    negative = true;
    s = s.slice(1, -1);
  }
  if (s.startsWith('-')) {
    negative = !negative;
    s = s.slice(1);
  } else if (s.endsWith('-')) {
    negative = !negative;
    s = s.slice(0, -1);
  }
  s = decimalComma ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(s)) throw new Error(`Invalid amount: ${raw}`);
  const cents = Math.round(Number(s) * 100);
  return negative ? -cents : cents;
}

export function parseDate(raw: string, format: BankCsvMapping['dateFormat']): string {
  const parts = raw.trim().split(/[-/.]/);
  if (parts.length !== 3) throw new Error(`Invalid date: ${raw}`);
  const [a, b, c] = parts as [string, string, string];
  const [y, m, d] =
    format === 'YYYY-MM-DD' || format === 'YYYY/MM/DD' ? [a, b, c] : format === 'MM/DD/YYYY' ? [c, a, b] : [c, b, a];
  const iso = `${y.padStart(4, '20')}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== iso) throw new Error(`Invalid date: ${raw}`);
  return iso;
}

/** Applies a mapping to parsed rows. Rows that cannot be read are reported, not silently dropped. */
export function toStatementLines(
  rows: string[][],
  mapping: BankCsvMapping,
): { lines: StatementLine[]; errors: { row: number; message: string }[] } {
  const lines: StatementLine[] = [];
  const errors: { row: number; message: string }[] = [];
  rows.slice(mapping.skipRows).forEach((cells, i) => {
    const row = i + mapping.skipRows + 1;
    try {
      const cell = (index: number | undefined) => (index === undefined ? '' : (cells[index] ?? ''));
      let amount: number | null;
      if (mapping.amountColumn !== undefined) {
        amount = parseAmount(cell(mapping.amountColumn), mapping.decimalComma);
      } else {
        const withdrawal = parseAmount(cell(mapping.withdrawalColumn), mapping.decimalComma);
        const deposit = parseAmount(cell(mapping.depositColumn), mapping.decimalComma);
        amount = (deposit ?? 0) - Math.abs(withdrawal ?? 0);
      }
      if (!amount) return; // zero or empty: informational line (e.g. opening balance)
      const description = mapping.descriptionColumns.map(cell).filter(Boolean).join(' ').replace(/\s+/g, ' ');
      lines.push({
        row,
        date: parseDate(cell(mapping.dateColumn), mapping.dateFormat),
        description: description || '—',
        reference: cell(mapping.referenceColumn) || null,
        amountCents: amount,
      });
    } catch (e) {
      errors.push({ row, message: e instanceof Error ? e.message : String(e) });
    }
  });
  return { lines, errors };
}

/** Best-effort guess of the mapping from a header row (French or English column names). */
export function guessMapping(header: string[]): Partial<BankCsvMapping> {
  const find = (...patterns: RegExp[]) => {
    const i = header.findIndex((h) => patterns.some((p) => p.test(h.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase())));
    return i >= 0 ? i : undefined;
  };
  const date = find(/^date/, /transaction date/);
  const description = find(/descr/, /libelle/, /detail/, /memo/, /nature/);
  const amount = find(/^montant$/, /^amount$/);
  const withdrawal = find(/retrait/, /debit/, /withdraw/);
  const deposit = find(/depot/, /credit/, /deposit/);
  const reference = find(/^ref/, /numero/, /cheque/);
  return {
    skipRows: 1,
    ...(date !== undefined ? { dateColumn: date } : {}),
    ...(description !== undefined ? { descriptionColumns: [description] } : {}),
    ...(amount !== undefined ? { amountColumn: amount } : withdrawal !== undefined && deposit !== undefined ? { withdrawalColumn: withdrawal, depositColumn: deposit } : {}),
    ...(reference !== undefined ? { referenceColumn: reference } : {}),
  };
}
